import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGlobalState } from "../context/GlobalStateContext";
import CharacterBackground from "../components/CharacterBackground";
import CRUDTable from "../components/CRUDTable/CRUDTable";
import CharacterEditModal from "../components/CharacterEditModal/CharacterEditModal";
import * as adminService from "../services/admin";
import "./AdminPanel.css";

const AdminPanel = () => {
  const { sessionInfo } = useGlobalState();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // State per ogni entità
  const [users, setUsers] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [equipmentTemplates, setEquipmentTemplates] = useState([]);
  const [weaponTemplates, setWeaponTemplates] = useState([]);

  // State per modal personaggio
  const [editingCharacter, setEditingCharacter] = useState(null);

  // Leggi il tab dall'hash o usa 'users' come default
  const getTabFromHash = () => {
    const hash = window.location.hash.slice(1); // Rimuovi il '#'
    const validTabs = ["users", "characters", "equipment", "weapons"];
    return validTabs.includes(hash) ? hash : "users";
  };

  const [activeTab, setActiveTab] = useState(getTabFromHash());

  // Sincronizza il tab con l'hash all'avvio e quando cambia l'hash
  useEffect(() => {
    const handleHashChange = () => {
      setActiveTab(getTabFromHash());
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => {
    // Controlla se l'utente è loggato e se è "gennaro"
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    if (sessionInfo.user && sessionInfo.user.username !== "gennaro") {
      alert("Accesso negato");
      navigate("/characters/list");
    }
  }, [sessionInfo, navigate]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Funzione per cambiare tab e aggiornare l'hash
  const changeTab = (tab) => {
    window.location.hash = tab;
    setActiveTab(tab);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case "users":
          const usersData = await adminService.getAllUsers();
          setUsers(usersData.users || []);
          break;
        case "characters":
          const charsData = await adminService.getAllCharacters();
          setCharacters(charsData.characters || []);
          break;
        case "equipment":
          const equipData = await adminService.getAllEquipmentTemplates();
          setEquipmentTemplates(equipData.equipmentTemplates || []);
          break;
        case "weapons":
          const weaponsData = await adminService.getAllWeaponTemplates();
          setWeaponTemplates(weaponsData.weaponTemplates || []);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Error loading data:", error);
      alert("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  // ========== USERS HANDLERS ==========
  const handleEditUser = async (id, userData) => {
    try {
      await adminService.updateUser(id, userData);
      alert("Utente aggiornato con successo");
      loadData();
    } catch (error) {
      console.error("Error updating user:", error);
      alert("Errore nell'aggiornamento dell'utente");
    }
  };

  const handleDeleteUser = async (id) => {
    try {
      await adminService.deleteUser(id);
      alert("Utente eliminato con successo");
      loadData();
    } catch (error) {
      console.error("Error deleting user:", error);
      alert("Errore nell'eliminazione dell'utente");
    }
  };

  // ========== CHARACTERS HANDLERS ==========
  const handleEditCharacterClick = (character) => {
    setEditingCharacter(character);
  };

  const handleSaveCharacter = async (characterData) => {
    try {
      await adminService.updateCharacter(characterData._id, characterData);
      alert("Personaggio aggiornato con successo");
      setEditingCharacter(null);
      loadData();
    } catch (error) {
      console.error("Error updating character:", error);
      alert("Errore nell'aggiornamento del personaggio");
      throw error;
    }
  };

  const handleDeleteCharacter = async (id) => {
    try {
      await adminService.deleteCharacter(id);
      alert("Personaggio eliminato con successo");
      loadData();
    } catch (error) {
      console.error("Error deleting character:", error);
      alert("Errore nell'eliminazione del personaggio");
    }
  };

  // ========== EQUIPMENT HANDLERS ==========
  const handleCreateEquipment = async (equipmentData) => {
    try {
      await adminService.createEquipmentTemplate(equipmentData);
      alert("Equipaggiamento creato con successo");
      loadData();
    } catch (error) {
      console.error("Error creating equipment:", error);
      alert("Errore nella creazione dell'equipaggiamento");
    }
  };

  const handleEditEquipment = async (id, equipmentData) => {
    try {
      await adminService.updateEquipmentTemplate(id, equipmentData);
      alert("Equipaggiamento aggiornato con successo");
      loadData();
    } catch (error) {
      console.error("Error updating equipment:", error);
      alert("Errore nell'aggiornamento dell'equipaggiamento");
    }
  };

  const handleDeleteEquipment = async (id) => {
    try {
      await adminService.deleteEquipmentTemplate(id);
      alert("Equipaggiamento eliminato con successo");
      loadData();
    } catch (error) {
      console.error("Error deleting equipment:", error);
      alert("Errore nell'eliminazione dell'equipaggiamento");
    }
  };

  // ========== WEAPONS HANDLERS ==========
  const handleCreateWeapon = async (weaponData) => {
    try {
      await adminService.createWeaponTemplate(weaponData);
      alert("Arma creata con successo");
      loadData();
    } catch (error) {
      console.error("Error creating weapon:", error);
      alert("Errore nella creazione dell'arma");
    }
  };

  const handleEditWeapon = async (id, weaponData) => {
    try {
      await adminService.updateWeaponTemplate(id, weaponData);
      alert("Arma aggiornata con successo");
      loadData();
    } catch (error) {
      console.error("Error updating weapon:", error);
      alert("Errore nell'aggiornamento dell'arma");
    }
  };

  const handleDeleteWeapon = async (id) => {
    try {
      await adminService.deleteWeaponTemplate(id);
      alert("Arma eliminata con successo");
      loadData();
    } catch (error) {
      console.error("Error deleting weapon:", error);
      alert("Errore nell'eliminazione dell'arma");
    }
  };

  // Definizioni colonne per ogni entità
  const usersColumns = [
    { field: "username", label: "Username", editable: false },
    { field: "email", label: "Email", editable: false },
    { field: "isMaster", label: "Master", type: "boolean" },
    { field: "isAdmin", label: "Admin", type: "boolean" },
  ];

  const charactersColumns = [
    { field: "playerName", label: "Nome Giocatore", editable: false },
    {
      field: "characterInfo",
      label: "Nome Personaggio",
      editable: false,
      render: (value) => value?.nome || "-",
    },
    {
      field: "user",
      label: "Utente",
      editable: false,
      render: (value) => value?.username || "-",
    },
    {
      field: "createdAt",
      label: "Data Creazione",
      editable: false,
      render: (value) => new Date(value).toLocaleDateString("it-IT"),
    },
  ];

  const equipmentCategories = [
    "ABBIGLIAMENTO",
    "EQUIPAGGIAMENTO MEDICO",
    "EQUIPAGGIAMENTO DA VIAGGIO E CAMPEGGIO",
    "ATTREZZI",
    "ATTREZZI PER INVESTIGATORI",
    "TRASPORTI",
    "COMUNICAZIONI",
    "INTRATTENIMENTO",
    "VARIE",
  ];

  const equipmentColumns = [
    { field: "name", label: "Nome" },
    {
      field: "category",
      label: "Categoria",
      type: "select",
      options: equipmentCategories,
    },
    { field: "cost", label: "Costo" },
    { field: "description", label: "Descrizione", type: "textarea" },
  ];

  const weaponCategories = [
    "ARMI DA CORPO A CORPO",
    "PISTOLE",
    "FUCILI",
    "SHOTGUN",
    "PISTOLE MITRAGLIATRICI",
    "MITRAGLIATRICI",
    "ESPLOSIVI E ARMI PESANTI",
  ];

  const weaponColumns = [
    { field: "name", label: "Nome" },
    {
      field: "category",
      label: "Categoria",
      type: "select",
      options: weaponCategories,
    },
    { field: "baseSkill", label: "Abilità Base" },
    { field: "baseSkillValue", label: "Valore Abilità", type: "number" },
    { field: "damage", label: "Danno" },
    { field: "range", label: "Gittata" },
    { field: "attacksPerRound", label: "Attacchi/Round" },
    { field: "magazine", label: "Caricatore" },
    { field: "cost", label: "Costo" },
    { field: "malfunzionamento", label: "Malf." },
  ];

  return (
    <div className="admin-panel page-wrapper">
      <CharacterBackground />
      <div className="admin-panel-content page-content">
        <div className="admin-header">
          <h1>Pannello di Amministrazione</h1>
          <p>
            Gestione flags utenti (Master/Admin), personaggi, equipaggiamento e
            armi
          </p>
        </div>

        <div className="admin-tabs">
          <button
            className={activeTab === "users" ? "active" : ""}
            onClick={() => changeTab("users")}
          >
            Utenti
          </button>
          <button
            className={activeTab === "characters" ? "active" : ""}
            onClick={() => changeTab("characters")}
          >
            Personaggi
          </button>
          <button
            className={activeTab === "equipment" ? "active" : ""}
            onClick={() => changeTab("equipment")}
          >
            Equipaggiamento
          </button>
          <button
            className={activeTab === "weapons" ? "active" : ""}
            onClick={() => changeTab("weapons")}
          >
            Armi
          </button>
        </div>

        <div className="admin-content">
          {loading ? (
            <div className="loading-state">Caricamento...</div>
          ) : (
            <>
              {activeTab === "users" && (
                <CRUDTable
                  data={users}
                  columns={usersColumns}
                  onEdit={handleEditUser}
                  onDelete={handleDeleteUser}
                  canCreate={false}
                />
              )}

              {activeTab === "characters" && (
                <>
                  <CRUDTable
                    data={characters}
                    columns={charactersColumns}
                    onDelete={handleDeleteCharacter}
                    canCreate={false}
                    canEdit={false}
                    renderCustomCell={(column, item) => {
                      if (column.field === "playerName") {
                        return (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <span>{item.playerName}</span>
                            <button
                              onClick={() => handleEditCharacterClick(item)}
                              className="btn-edit"
                              style={{ padding: "4px 10px", fontSize: "11px" }}
                            >
                              Modifica
                            </button>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {editingCharacter && (
                    <CharacterEditModal
                      character={editingCharacter}
                      onClose={() => setEditingCharacter(null)}
                      onSave={handleSaveCharacter}
                    />
                  )}
                </>
              )}

              {activeTab === "equipment" && (
                <CRUDTable
                  data={equipmentTemplates}
                  columns={equipmentColumns}
                  onCreate={handleCreateEquipment}
                  onEdit={handleEditEquipment}
                  onDelete={handleDeleteEquipment}
                />
              )}

              {activeTab === "weapons" && (
                <CRUDTable
                  data={weaponTemplates}
                  columns={weaponColumns}
                  onCreate={handleCreateWeapon}
                  onEdit={handleEditWeapon}
                  onDelete={handleDeleteWeapon}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
